import { Button } from '@aod/ui/components/ui/button';
import { Gamepad2, Keyboard, LayoutGrid, Monitor, type Settings, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type MessageKey, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { AudioTab } from './tabs/AudioTab.js';
import { ControlsTab } from './tabs/ControlsTab.js';
import { GameplayTab } from './tabs/GameplayTab.js';
import { GraphicsTab } from './tabs/GraphicsTab.js';
import { InterfaceTab } from './tabs/InterfaceTab.js';
import { useSettings } from './useSettings';

type Tab = 'audio' | 'controls' | 'graphics' | 'gameplay' | 'interface';

const TABS: { id: Tab; label: MessageKey; Icon: typeof Settings }[] = [
  { id: 'audio', label: 'settings.tab.audio', Icon: Volume2 },
  { id: 'controls', label: 'settings.tab.controls', Icon: Keyboard },
  { id: 'graphics', label: 'settings.tab.graphics', Icon: Monitor },
  { id: 'gameplay', label: 'settings.tab.gameplay', Icon: Gamepad2 },
  { id: 'interface', label: 'settings.tab.interface', Icon: LayoutGrid },
];

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function SettingsModal({ open, onClose }: Props) {
  const t = useT();
  const [tab, setTab] = useState<Tab>('audio');
  const overlayRef = useRef<HTMLDivElement>(null);

  const trapFocus = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const el = overlayRef.current;
      if (!el) return;
      const focusable = el.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', trapFocus);
    return () => window.removeEventListener('keydown', trapFocus);
  }, [open, trapFocus]);

  // Apply master volume to SoundManager singleton.
  const masterVolume = useSettings((s) => s.masterVolume);

  useEffect(() => {
    if (!open) return;
    import('../audio/useSounds')
      .then(({ getSoundManager }) => {
        getSoundManager().setMasterVolume(masterVolume);
      })
      .catch(() => {});
  }, [masterVolume, open]);

  if (!open) return null;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape/Tab handled via global trapFocus listener in useEffect above.
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: custom overlay with its own focus trap and Escape handling */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('settings.open')}
        data-testid="settings-modal"
        {...appDom({
          id: 'settings-modal',
          type: 'Modal',
          scope: 'dialog',
          label: t('settings.open'),
        })}
        className="w-full max-w-md rounded-xl border border-border/50 bg-card shadow-2xl animate-fade-in-scale"
      >
        {/* Tab bar */}
        <div className="flex border-b border-border/30">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              data-testid={`settings-tab-${id}`}
              {...appDom({
                id: `settings-tab-${id}`,
                type: 'Tabs',
                role: 'button',
                scope: 'dialog',
              })}
              className={`flex-1 flex cursor-pointer items-center justify-center gap-1.5 px-3 py-3 text-xs font-medium transition-colors ${
                tab === id
                  ? 'text-foreground border-b-2 border-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/30'
              }`}
            >
              <Icon className="size-3.5" aria-hidden />
              {t(label)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-6">
          {tab === 'audio' && <AudioTab />}
          {tab === 'controls' && <ControlsTab />}
          {tab === 'graphics' && <GraphicsTab />}
          {tab === 'gameplay' && <GameplayTab />}
          {tab === 'interface' && <InterfaceTab />}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-border/30 px-6 py-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            data-testid="settings-close"
            {...appDom({ id: 'settings-close', type: 'Button', role: 'button', scope: 'dialog' })}
            className="text-xs"
          >
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
